import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdCardMembership } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/owner/PageHeader';
import GymIdMemberSearch from '../../components/receptionist/GymIdMemberSearch';
import { usePanelToast } from '../../context/PanelToastContext';
import { formatCurrency } from './receptionistData';
import { addSubscriptionPayment } from './paymentStore';
import {
  fetchMembersPage,
  fetchMemberById,
  findMemberByGymMemberId,
  fetchMembershipsForMember,
  fetchPackages,
  peekNextMembershipSerialNo,
} from '../../services/receptionist/receptionistApi';
import {
  ensureWalkInMember,
  isWalkInMember,
  WALK_IN_GYM_MEMBER_ID,
  WALK_IN_PAYMENT_NAME,
} from '../../services/receptionist/walkInMember';
import { getReceptionistBranch, RECEPTIONIST_BRANCH_EVENT, isReceptionistPanelScope } from '../../services/gym/branchContext';
import { scopedReceptionistBranch, useSaleWriteBranch, SaleBranchField } from '../../hooks/useSaleWriteBranch';
import { dedupeMembers, dedupeByName } from '../../utils/dedupeList';
import { getGymMemberId, formatSerialDisplay } from '../admin/members/memberData';
import {
  memberHasAnyPriorMembership,
  memberHasUnexpiredSubscription,
} from '../../utils/membershipEligibility';
import {
  exportSubscriptionReceiptCsv,
  exportSubscriptionReceiptExcel,
  exportSubscriptionReceiptPdf,
  printSubscriptionReceipt,
} from './exportReceipt';
import { withReceiptPrintSerial } from '../../services/receptionist/receiptSerial';
import { InvoiceTermsNotes } from './InvoiceTermsNotes';
import SaleAmountFields from './sales/SaleAmountFields';
import {
  SUBSCRIPTION_DURATION_OPTIONS,
  addMonthsToEndDate,
  subscriptionDurationLabel,
} from './sales/saleDurationUtils';
import {
  combineMethodLabels,
  defaultPaymentSplits,
  paymentMethodLabel,
  serializePaymentSplits,
  sumPaymentSplits,
} from '../../utils/splitPaymentUtils';
import '../owner/ownerPages.css';
import '../owner/subscriptions/subscriptions.css';
import './members/members.css';
import './payments.css';
import './sales.css';

const WALK_IN_CUSTOMER = WALK_IN_PAYMENT_NAME;
const SUBSCRIPTION_MONTH_VALUES = SUBSCRIPTION_DURATION_OPTIONS.map((o) => o.value);

function parseDurationMonths(duration) {
  if (!duration || duration === 'Lifetime') return null;
  const match = String(duration).match(/(\d+)/);
  return match ? Number(match[1]) : 1;
}

function durationMonthsFromPackage(pkg) {
  const months = parseDurationMonths(pkg?.duration);
  if (months != null && SUBSCRIPTION_MONTH_VALUES.includes(months)) return months;
  return 1;
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
}

function packageOptionLabel(pkg) {
  const name = pkg?.name || 'Unnamed package';
  const suggested = Number(pkg?.price) || 0;
  return suggested > 0 ? `${name} (suggest ${formatCurrency(suggested)})` : name;
}

function durationLabel(months) {
  return subscriptionDurationLabel(months) || `${months} months`;
}

function InvoiceSummary({ receipt }) {
  return (
    <div className="rec-payment-receipt-preview">
      <div className="rec-payment-receipt-row">
        <span>CR. No.</span>
        <strong>{receipt.invoiceId || '—'}</strong>
      </div>
      {!receipt.complimentary && (
        <div className="rec-payment-receipt-row">
          <span>Payment ID</span>
          <strong>{receipt.paymentId}</strong>
        </div>
      )}
      <div className="rec-payment-receipt-row">
        <span>Customer</span>
        <strong>{receipt.customer}</strong>
      </div>
      <div className="rec-payment-receipt-row">
        <span>Mobile</span>
        <strong>{receipt.phone || receipt.mobile || receipt.customerPhone || '—'}</strong>
      </div>
      <div className="rec-payment-receipt-row">
        <span>Member Serial No</span>
        <strong>{receipt.gymId || receipt.customerGymId || receipt.serialNo || '—'}</strong>
      </div>
      <div className="rec-payment-receipt-row">
        <span>Package</span>
        <strong>{receipt.package}</strong>
      </div>
      <div className="rec-payment-receipt-row">
        <span>Duration</span>
        <strong>{receipt.durationLabel || '—'}</strong>
      </div>
      <div className="rec-payment-receipt-row">
        <span>Package Price</span>
        <strong>
          {receipt.complimentary ? 'Complimentary' : formatCurrency(receipt.packagePrice)}
        </strong>
      </div>
      {!receipt.complimentary && receipt.discountAmount > 0 && (
        <div className="rec-payment-receipt-row">
          <span>Discount ({receipt.discountPercent}%)</span>
          <strong>− {formatCurrency(receipt.discountAmount)}</strong>
        </div>
      )}
      {!receipt.complimentary && receipt.registrationFee > 0 && (
        <div className="rec-payment-receipt-row">
          <span>Registration Fee</span>
          <strong>{formatCurrency(receipt.registrationFee)}</strong>
        </div>
      )}
      {!receipt.complimentary && receipt.personalTraining > 0 && (
        <div className="rec-payment-receipt-row">
          <span>
            Personal Training{receipt.trainer ? ` (${receipt.trainer})` : ''}
          </span>
          <strong>{formatCurrency(receipt.personalTraining)}</strong>
        </div>
      )}
      {!receipt.complimentary && receipt.classesFee > 0 && (
        <div className="rec-payment-receipt-row">
          <span>
            Classes{receipt.className ? ` (${receipt.className})` : ''}
          </span>
          <strong>{formatCurrency(receipt.classesFee)}</strong>
        </div>
      )}
      <div className="rec-payment-receipt-row">
        <span>Total Amount</span>
        <strong>
          {receipt.complimentary
            ? 'Complimentary'
            : formatCurrency(receipt.finalAmount ?? receipt.totalAmount)}
        </strong>
      </div>
      <div className="rec-payment-receipt-row">
        <span>Entry Date</span>
        <strong>{receipt.entryDate || receipt.date}</strong>
      </div>
      <div className="rec-payment-receipt-row">
        <span>Membership Start</span>
        <strong>{receipt.startDate}</strong>
      </div>
      <div className="rec-payment-receipt-row">
        <span>Membership End</span>
        <strong>{receipt.endDate}</strong>
      </div>
      {receipt.paymentSplits?.length ? (
        receipt.paymentSplits.map((split) => (
          <div key={split.method} className="rec-payment-receipt-row">
            <span>{paymentMethodLabel(split.method)}</span>
            <strong>{formatCurrency(split.amount)}</strong>
          </div>
        ))
      ) : (
        <div className="rec-payment-receipt-row">
          <span>Payment Type</span>
          <strong>{receipt.complimentary ? 'Complimentary' : receipt.paymentType}</strong>
        </div>
      )}
      {Number(receipt.totalLeft ?? receipt.pendingAmount) > 0 && (
        <div className="rec-payment-receipt-row">
          <span>Pending Amount</span>
          <strong>{formatCurrency(receipt.totalLeft ?? receipt.pendingAmount)}</strong>
        </div>
      )}
      <div className="rec-payment-receipt-row rec-payment-receipt-row-highlight">
        <span>Amount Paid</span>
        <strong>
          {receipt.complimentary ? 'Complimentary' : formatCurrency(receipt.paymentAmount)}
        </strong>
      </div>
      {receipt.paymentNote && (
        <div className="rec-payment-receipt-row">
          <span>Payment Note</span>
          <strong>{receipt.paymentNote}</strong>
        </div>
      )}
      <InvoiceTermsNotes />
    </div>
  );
}

function Subscriptions() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = usePanelToast();
  const incomingCustomer = location.state?.customer || '';
  const incomingCustomerId = location.state?.customerId || '';
  const incomingGymId = location.state?.customerGymId || '';
  const preselectedPackage = location.state?.packageName || '';

  const [packages, setPackages] = useState([]);
  const [memberHits, setMemberHits] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberMemberships, setMemberMemberships] = useState([]);
  const [assignedBranch, setAssignedBranch] = useState(() => scopedReceptionistBranch());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [incomingHandled, setIncomingHandled] = useState(false);
  const incomingLockRef = React.useRef(false);
  const submittingRef = React.useRef(false);
  const [submitting, setSubmitting] = useState(false);

  const today = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();

  const loadData = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const branch = scopedReceptionistBranch();
      setAssignedBranch(branch);
      const pkgData = await fetchPackages(branch);
      setPackages(Array.isArray(pkgData) ? dedupeByName(pkgData) : []);
    } catch (err) {
      setError(err.message || 'Failed to load subscription data');
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const onBranchChange = (event) => {
      const next = isReceptionistPanelScope()
        ? (event?.detail?.branch ?? getReceptionistBranch())
        : '';
      setAssignedBranch((prev) => {
        if (prev === (next || '')) return prev;
        loadData();
        return next || '';
      });
    };
    window.addEventListener(RECEPTIONIST_BRANCH_EVENT, onBranchChange);
    return () => window.removeEventListener(RECEPTIONIST_BRANCH_EVENT, onBranchChange);
  }, [loadData]);

  const activePackages = useMemo(
    () => packages.filter((p) => !p.status || p.status === 'active'),
    [packages],
  );

  const [customer, setCustomer] = useState('');
  const [customerGymId, setCustomerGymId] = useState('');
  const {
    writeBranch,
    branches: writeBranches,
    pickedBranch,
    setPickedBranch,
    showPicker: showBranchPicker,
  } = useSaleWriteBranch(selectedMember, customerGymId);
  const [gymIdQuery, setGymIdQuery] = useState('');
  const [packageId, setPackageId] = useState('');
  const [packagePriceInput, setPackagePriceInput] = useState('');
  const [durationMonths, setDurationMonths] = useState(1);
  const [entryDate, setEntryDate] = useState(today);
  const [startDate, setStartDate] = useState(today);
  const [datesTbc, setDatesTbc] = useState(false);
  const [complimentary, setComplimentary] = useState(false);
  const [discountPercent, setDiscountPercent] = useState('');
  const [registrationFeeEnabled, setRegistrationFeeEnabled] = useState(true);
  const [registrationFeeAmount, setRegistrationFeeAmount] = useState('0');
  const [paymentSplits, setPaymentSplits] = useState(() => defaultPaymentSplits());
  const [paymentNote, setPaymentNote] = useState('');
  const splitsTouchedRef = React.useRef(false);
  const [receipt, setReceipt] = useState(null);
  const [saved, setSaved] = useState(false);

  // Server-searched members eligible for a new subscription (no active package).
  const eligibleMembers = memberHits;

  const selectedPackage = useMemo(
    () => activePackages.find((p) => p.id === packageId) || activePackages[0] || null,
    [packageId, activePackages],
  );

  // Registration fee only for first package ever — never again for that member
  const isFirstTimeMember = useMemo(() => {
    if (!customer) return false;
    if (customer === WALK_IN_CUSTOMER) return true;
    if (!selectedMember) return true;
    return !memberHasAnyPriorMembership(selectedMember, memberMemberships);
  }, [customer, selectedMember, memberMemberships]);

  const [nextPackageSerial, setNextPackageSerial] = useState(null);

  // Preview next serial from the selected package series (start → max + 1)
  useEffect(() => {
    if (!selectedPackage?.name || !isFirstTimeMember) {
      setNextPackageSerial(null);
      return undefined;
    }
    let cancelled = false;
    peekNextMembershipSerialNo(selectedPackage.name)
      .then((n) => {
        if (!cancelled) setNextPackageSerial(n);
      })
      .catch(() => {
        if (!cancelled) setNextPackageSerial(null);
      });
    return () => { cancelled = true; };
  }, [selectedPackage?.name, isFirstTimeMember, packageId]);

  // Search members as the receptionist types (name, serial, phone).
  useEffect(() => {
    if (customer === WALK_IN_CUSTOMER) {
      setMemberHits([]);
      return undefined;
    }
    const q = gymIdQuery.trim();
    if (!q || (selectedMember && (selectedMember.gymId === q || selectedMember.name === q || getGymMemberId(selectedMember) === q))) {
      if (selectedMember) setMemberHits([selectedMember]);
      else setMemberHits([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await fetchMembersPage({ search: q, pageSize: 25 });
        if (cancelled) return;
        setMemberHits(dedupeMembers(result.rows || []));
      } catch {
        if (!cancelled) setMemberHits([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [gymIdQuery, customer, selectedMember]);

  // Load this member's memberships when selected (registration fee + active checks).
  useEffect(() => {
    if (!selectedMember?.id) {
      setMemberMemberships([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchMembershipsForMember(selectedMember.id);
        if (!cancelled) setMemberMemberships(rows);
      } catch {
        if (!cancelled) setMemberMemberships([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedMember?.id]);

  useEffect(() => {
    if (!activePackages.length) {
      setPackageId('');
      return;
    }
    const stillValid = activePackages.some((p) => p.id === packageId);
    if (!packageId || !stillValid) {
      setPackageId(activePackages[0].id);
    }
  }, [activePackages, packageId]);

  // Prefill editable price + duration when package changes
  useEffect(() => {
    if (!selectedPackage) {
      setPackagePriceInput('');
      setDurationMonths(1);
      return;
    }
    const catalogPrice = Number(selectedPackage.price);
    setPackagePriceInput(Number.isFinite(catalogPrice) ? String(catalogPrice) : '');
    setDurationMonths(durationMonthsFromPackage(selectedPackage));
  }, [selectedPackage?.id]);

  // Incoming member from Members → Subscription button
  useEffect(() => {
    if (incomingHandled || incomingLockRef.current || loading) return;
    if (!incomingCustomer && !incomingCustomerId && !incomingGymId) {
      setIncomingHandled(true);
      return;
    }

    incomingLockRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        let match = null;
        if (incomingCustomerId) {
          match = await fetchMemberById(incomingCustomerId);
        }
        if (!match && incomingGymId) {
          match = await findMemberByGymMemberId(incomingGymId);
        }
        if (!match && incomingCustomer) {
          const result = await fetchMembersPage({ search: incomingCustomer, pageSize: 5 });
          match = (result.rows || []).find((m) => m.name === incomingCustomer) || (result.rows || [])[0] || null;
        }
        if (cancelled) return;

        const preferredName = preselectedPackage || '';
        const preferredPkg = preferredName
          ? activePackages.find((p) => p.name === preferredName)
          : null;
        const nextPkg = preferredPkg || activePackages[0] || null;
        if (nextPkg) setPackageId(nextPkg.id);

        if (match) {
          const rows = await fetchMembershipsForMember(match.id).catch(() => []);
          if (cancelled) return;
          if (memberHasUnexpiredSubscription(match, rows)) {
            setCustomer('');
            setCustomerGymId('');
            setGymIdQuery('');
            setSelectedMember(null);
            setMemberMemberships([]);
            setError(`${match.name} already has an active subscription. They can subscribe again after it expires.`);
            toast.showError('This member already has an active subscription.');
          } else {
            setSelectedMember(match);
            setMemberMemberships(rows);
            setMemberHits([match]);
            setCustomer(match.name);
            setCustomerGymId(match.gymId || '');
            setGymIdQuery(match.gymId || match.name);
            setError('');
          }
        } else {
          setCustomer('');
          setCustomerGymId('');
          setGymIdQuery('');
          setSelectedMember(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load member');
      } finally {
        if (!cancelled) {
          setIncomingHandled(true);
          navigate(location.pathname, { replace: true, state: null });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [
    incomingHandled,
    loading,
    incomingCustomer,
    incomingCustomerId,
    incomingGymId,
    preselectedPackage,
    activePackages,
    navigate,
    location.pathname,
    toast,
  ]);

  useEffect(() => {
    if (!selectedMember || customer === WALK_IN_CUSTOMER) return;
    if (memberHasUnexpiredSubscription(selectedMember, memberMemberships)) {
      setCustomer('');
      setCustomerGymId('');
      setGymIdQuery('');
      setSelectedMember(null);
      setError(`${selectedMember.name} already has an active subscription. They can subscribe again after it expires.`);
    }
  }, [selectedMember, memberMemberships, customer]);

  const packagePrice = Math.max(0, Number(packagePriceInput) || 0);

  useEffect(() => {
    if (isFirstTimeMember && selectedPackage) {
      setRegistrationFeeEnabled(true);
      const regFee = Number(selectedPackage.registrationFee);
      setRegistrationFeeAmount(regFee > 0 ? String(regFee) : '');
    } else {
      setRegistrationFeeEnabled(false);
      setRegistrationFeeAmount('');
    }
  }, [isFirstTimeMember, selectedPackage]);

  const endDate = useMemo(() => {
    const months = Number(durationMonths) || 1;
    return addMonthsToEndDate(startDate, months);
  }, [startDate, durationMonths]);

  const endDateLabel = formatDisplayDate(endDate);
  const billingAddress = customer || '—';
  const selectedDurationLabel = durationLabel(durationMonths);

  const discountAmount = useMemo(() => {
    const percent = Math.min(100, Math.max(0, Number(discountPercent) || 0));
    return Math.min(packagePrice, (packagePrice * percent) / 100);
  }, [discountPercent, packagePrice]);

  const registrationFeeTotal = complimentary
    ? 0
    : (isFirstTimeMember && registrationFeeEnabled
      ? Math.max(0, Number(registrationFeeAmount) || 0)
      : 0);

  const chargeablePackagePrice = complimentary ? 0 : packagePrice;
  const chargeableDiscount = complimentary ? 0 : discountAmount;

  const finalAmount = complimentary
    ? 0
    : Math.max(0, packagePrice - discountAmount + registrationFeeTotal);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!selectedPackage) {
      setError('No package selected. Create a package in Admin → Packages first.');
      return;
    }
    if (!complimentary) {
      const priceRaw = String(packagePriceInput ?? '').trim();
      // Blank package price is allowed — save now, edit later
      if (priceRaw !== '' && (!Number.isFinite(packagePrice) || packagePrice < 0)) {
        setError('Enter a valid package price, or leave blank to set later.');
        return;
      }
    }
    if (!customer.trim()) {
      setError('Search and select a member by Serial No before adding a package.');
      return;
    }
    if (showBranchPicker && writeBranches.length > 0 && !writeBranch) {
      setError('Select a branch for this sale.');
      toast.showError('Select a branch for this sale.');
      return;
    }
    if (selectedMember && !isWalkInMember(selectedMember)
      && memberHasUnexpiredSubscription(selectedMember, memberMemberships)) {
      setError(`${selectedMember.name} already has an active subscription. Wait until it expires before selling a new package.`);
      return;
    }

    const priceBlank = !complimentary && String(packagePriceInput ?? '').trim() === '';
    const paidAmount = complimentary ? 0 : sumPaymentSplits(paymentSplits);
    const paymentStatus = complimentary
      ? 'complimentary'
      : (finalAmount <= 0 || paidAmount >= finalAmount ? 'completed' : 'pending');
    const serializedSplits = complimentary ? [] : serializePaymentSplits(paymentSplits);
    const paymentTypeLabel = complimentary ? 'Complimentary' : combineMethodLabels(paymentSplits);
    if (!complimentary) {
      // 0 paid is allowed (pay later) — appears in sale/membership history, not Payments until paid
      if (!Number.isFinite(paidAmount) || paidAmount < 0) {
        setError('Enter a valid paid amount (0 is allowed for pay later).');
        return;
      }
      // When package price is blank, allow any collected amount (price set later on edit)
      if (!priceBlank && paidAmount > finalAmount) {
        setError('Total paid cannot exceed the package total.');
        return;
      }
    }

    const paymentId = complimentary ? '—' : `PAY-${String(Date.now()).slice(-6)}`;
    // Blank Membership Start → today (TBC only when checkbox is on)
    const resolvedStart = datesTbc
      ? null
      : (String(startDate || '').slice(0, 10) || String(entryDate || '').slice(0, 10) || today);
    const resolvedEnd = datesTbc
      ? null
      : (endDate || addMonthsToEndDate(resolvedStart, Number(durationMonths) || 1));
    const customerPhone = String(
      selectedMember?.phone || selectedMember?.mobile || '',
    ).trim();
    const receiptData = {
      invoiceId: '',
      paymentId,
      customer,
      phone: customerPhone || '—',
      mobile: customerPhone || '—',
      customerPhone: customerPhone || '—',
      package: selectedPackage.name,
      packagePrice: chargeablePackagePrice,
      durationLabel: selectedDurationLabel,
      discountAmount: chargeableDiscount,
      discountPercent: complimentary ? 0 : (Number(discountPercent) || 0),
      registrationFee: registrationFeeTotal,
      personalTraining: 0,
      trainer: null,
      classesFee: 0,
      className: null,
      finalAmount,
      totalAmount: finalAmount,
      totalLeft: 0,
      pendingAmount: 0,
      paymentAmount: paidAmount,
      paymentStatus,
      paymentType: paymentTypeLabel,
      paymentSplits: serializedSplits,
      paymentNote: complimentary ? (paymentNote || 'Complimentary subscription') : paymentNote,
      complimentary,
      startDate: datesTbc ? 'TBC' : formatDisplayDate(resolvedStart),
      endDate: datesTbc ? 'TBC' : formatDisplayDate(resolvedEnd),
      entryDate: formatDisplayDate(entryDate),
      datesTbc,
      date: formatDisplayDate(entryDate),
    };

    try {
      submittingRef.current = true;
      setSubmitting(true);
      setError('');
      // Blank start date → today (never save empty start unless TBC is explicitly checked)
      const saved = await addSubscriptionPayment({
        paymentId: complimentary ? '' : paymentId,
        customer,
        package: selectedPackage.name,
        paymentAmount: paidAmount,
        paymentType: paymentTypeLabel,
        paymentSplits: serializedSplits,
        paymentStatus,
        paymentNote: complimentary ? (paymentNote || 'Complimentary subscription') : paymentNote,
        date: entryDate,
        packagePrice: chargeablePackagePrice,
        discountPercent: complimentary ? 0 : (Number(discountPercent) || 0),
        discountAmount: chargeableDiscount,
        registrationFee: registrationFeeTotal,
        personalTraining: 0,
        classesFee: 0,
        expiryDate: datesTbc ? null : resolvedEnd,
        startDate: resolvedStart,
        datesTbc,
        gymMemberId: customerGymId || selectedMember?.gymId || '',
        memberId: selectedMember?.id || '',
        duration: selectedDurationLabel,
        durationMonths,
        complimentary,
        branch: writeBranch,
      });

      const invoiceId = complimentary
        ? ''
        : (saved?.invoiceId || '');
      if (invoiceId) receiptData.invoiceId = invoiceId;
      const pickSerial = (value) => {
        const raw = String(value ?? '').trim();
        if (!raw || raw === '—' || raw === '-') return '';
        return formatSerialDisplay(raw) || raw;
      };
      const serial = pickSerial(saved?.gymId)
        || pickSerial(saved?.serialNo)
        || pickSerial(customerGymId)
        || pickSerial(selectedMember?.gymId)
        || pickSerial(getGymMemberId(selectedMember));
      if (serial) {
        receiptData.gymId = serial;
        receiptData.customerGymId = serial;
        receiptData.serialNo = serial;
      }

      // Show invoice + success immediately, then clear the form
      setError('');
      setReceipt(withReceiptPrintSerial(receiptData));
      setSaved(true);
      toast.showAdded(complimentary ? 'Complimentary subscription' : 'Subscription');
      setCustomer('');
      setCustomerGymId('');
      setGymIdQuery('');
      setSelectedMember(null);
      setMemberMemberships([]);
      setMemberHits([]);
      setComplimentary(false);
      setEntryDate(today);
      setDatesTbc(false);
      setStartDate(today);
    } catch (err) {
      const message = err.message || 'Failed to complete sale';
      setError(message);
      const isAlready = /already has/i.test(message);
      toast.showError(message, isAlready ? 'Already subscribed' : 'Something went wrong');
      if (isAlready) {
        setCustomer('');
        setCustomerGymId('');
        setGymIdQuery('');
        setSelectedMember(null);
        setMemberMemberships([]);
        setMemberHits([]);
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    const firstPkg = packages[0];
    setCustomer('');
    setCustomerGymId('');
    setGymIdQuery('');
    setSelectedMember(null);
    setMemberMemberships([]);
    setMemberHits([]);
    setPackageId(firstPkg?.id || '');
    {
      const catalogPrice = Number(firstPkg?.price);
      setPackagePriceInput(Number.isFinite(catalogPrice) ? String(catalogPrice) : '');
    }
    setDurationMonths(durationMonthsFromPackage(firstPkg));
    setEntryDate(today);
    setStartDate(today);
    setDatesTbc(false);
    setComplimentary(false);
    setDiscountPercent('');
    setRegistrationFeeEnabled(true);
    {
      const regFee = Number(firstPkg?.registrationFee);
      setRegistrationFeeAmount(regFee > 0 ? String(regFee) : '');
    }
    setPaymentSplits(defaultPaymentSplits());
    setPaymentNote('');
    splitsTouchedRef.current = false;
    setReceipt(null);
    setSaved(false);
  };

  return (
    <div>
      <PageHeader
        title="Subscriptions"
        subtitle="Register sale (entry date) and set membership start — use TBC if gym not started yet"
        variant="banner"
      >
        {saved && receipt && (
          <span className="owner-badge owner-badge-success">
            Subscription completed — {receipt.invoiceId}
          </span>
        )}
      </PageHeader>

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && packages.length === 0 && (
        <p className="owner-placeholder-text owner-mb-16">Loading packages...</p>
      )}

      {isReceptionistPanelScope() && !assignedBranch && (
        <p className="owner-placeholder-text owner-mb-16">
          No branch assigned yet. You can still sell gym-wide packages. Ask your administrator to set your branch in Receptionist Management.
        </p>
      )}

      {!loading && activePackages.length === 0 && (
        <p className="owner-placeholder-text owner-mb-16">
          No packages available yet. Create packages in Admin → Packages, then refresh this page.
        </p>
      )}

      {(packages.length > 0 || !loading) && (
      <form onSubmit={handleSubmit}>
        <div className="rec-sales-layout">
          <div className="rec-sales-form-col">
          <div className="owner-card rec-panel-card rec-add-package-card">
            <h2 className="rec-add-package-title">Add Package</h2>

            <div className="rec-add-package-grid">
              <div className="rec-add-package-col">
                <div className="owner-form-group">
                  <GymIdMemberSearch
                    id="add-package-customer"
                    label="Customer (Serial No / name / phone / CNIC)"
                    className=""
                    query={gymIdQuery}
                    onQueryChange={(value) => {
                      setGymIdQuery(value);
                      if (selectedMember) {
                        setSelectedMember(null);
                        setCustomer('');
                        setCustomerGymId('');
                        setMemberMemberships([]);
                      }
                    }}
                    selectedName={customer === WALK_IN_CUSTOMER ? '' : customer}
                    selectedGymId={customerGymId}
                    onSelect={async ({ name, gymId, member }) => {
                      setError('');
                      if (!member) {
                        setCustomer(name || '');
                        setCustomerGymId(gymId || '');
                        setSelectedMember(null);
                        setMemberMemberships([]);
                        return;
                      }
                      try {
                        const rows = await fetchMembershipsForMember(member.id);
                        if (memberHasUnexpiredSubscription(member, rows)) {
                          setCustomer('');
                          setCustomerGymId('');
                          setGymIdQuery('');
                          setSelectedMember(null);
                          setMemberMemberships([]);
                          setMemberHits([]);
                          const msg = `${member.name} already has an active subscription. They can subscribe again after it expires.`;
                          setError(msg);
                          toast.showError('This member already has an active subscription.');
                          return;
                        }
                        setMemberMemberships(rows);
                      } catch {
                        setMemberMemberships([]);
                      }
                      setCustomer(name || member.name || '');
                      setCustomerGymId(gymId || getGymMemberId(member) || '');
                      if (gymId) setGymIdQuery(gymId);
                      setSelectedMember(member);
                      setMemberHits([member]);
                    }}
                    members={eligibleMembers}
                    serverFiltered
                    placeholder="Search serial no, name, phone, or CNIC..."
                  />
                  {gymIdQuery.trim() && eligibleMembers.length === 0 && !selectedMember && customer !== WALK_IN_CUSTOMER && (
                    <p className="owner-placeholder-text" style={{ marginTop: 8, fontSize: 12 }}>
                      No members match this serial no, name, phone, or CNIC.
                    </p>
                  )}
                  {showBranchPicker && (
                    <SaleBranchField
                      id="subscription-sale-branch"
                      branches={writeBranches}
                      value={pickedBranch}
                      onChange={setPickedBranch}
                    />
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, alignItems: 'center' }}>
                    <button
                      type="button"
                      className="owner-btn owner-btn-secondary owner-btn-sm"
                      onClick={async () => {
                        try {
                          const walkIn = await ensureWalkInMember();
                          setCustomer(WALK_IN_PAYMENT_NAME);
                          setCustomerGymId(WALK_IN_GYM_MEMBER_ID);
                          setGymIdQuery(WALK_IN_GYM_MEMBER_ID);
                          setSelectedMember(walkIn);
                          setMemberMemberships([]);
                          setMemberHits([walkIn]);
                        } catch (err) {
                          toast.showError(err.message || 'Could not load walk-in customer.');
                        }
                      }}
                    >
                      Walk-In Customer
                    </button>
                    {customer === WALK_IN_CUSTOMER && (
                      <span className="owner-placeholder-text" style={{ margin: 0, fontSize: 12 }}>
                        Walk-in selected (Serial {WALK_IN_GYM_MEMBER_ID})
                      </span>
                    )}
                  </div>
                </div>

                <div className="owner-form-group">
                  <label className="rec-add-package-label">Billing Address:</label>
                  <p className="rec-add-package-address">{billingAddress}</p>
                </div>

                <p className="rec-add-package-section-title">Registration / sale</p>
                <p className="rec-add-package-section-hint">
                  When the member registered or paid today — not when gym access starts.
                </p>

                <div className="owner-form-group">
                  <label className="rec-add-package-label" htmlFor="add-package-entry-date">
                    Entry Date
                  </label>
                  <input
                    id="add-package-entry-date"
                    type="date"
                    className="owner-form-input rec-add-package-input"
                    value={entryDate}
                    onChange={(e) => setEntryDate(e.target.value)}
                    required
                  />
                </div>

                <div className="owner-form-group">
                  <label className="rec-sub-summary-check rec-add-package-tbc-check">
                    <input
                      type="checkbox"
                      checked={complimentary}
                      onChange={(e) => setComplimentary(e.target.checked)}
                    />
                    <span>Complimentary — no payment required for this customer</span>
                  </label>
                </div>

                <p className="rec-add-package-section-title">Membership period (gym access)</p>
                <p className="rec-add-package-section-hint">
                  Start date is when the member begins using the gym. Use TBC if they have paid but not started yet.
                </p>

                <div className="owner-form-group">
                  <label className="rec-sub-summary-check rec-add-package-tbc-check">
                    <input
                      type="checkbox"
                      checked={datesTbc}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setDatesTbc(next);
                        if (!next && !startDate) setStartDate(today);
                      }}
                    />
                    <span>TBC — member has not started gym yet (set start date later in Memberships)</span>
                  </label>
                </div>

                <div className="owner-form-group">
                  <label className="rec-add-package-label" htmlFor="add-package-start-date">
                    Membership Start
                  </label>
                  {datesTbc ? (
                    <input
                      id="add-package-start-date"
                      type="text"
                      className="owner-form-input rec-add-package-input rec-add-package-input-readonly"
                      value="TBC"
                      readOnly
                      tabIndex={-1}
                    />
                  ) : (
                    <input
                      id="add-package-start-date"
                      type="date"
                      className="owner-form-input rec-add-package-input"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  )}
                </div>

                <div className="owner-form-group">
                  <label className="rec-add-package-label" htmlFor="add-package-end-date">
                    Membership End
                  </label>
                  {datesTbc ? (
                    <input
                      id="add-package-end-date"
                      type="text"
                      className="owner-form-input rec-add-package-input rec-add-package-input-readonly"
                      value="TBC"
                      readOnly
                      tabIndex={-1}
                    />
                  ) : (
                    <input
                      id="add-package-end-date"
                      type="date"
                      className="owner-form-input rec-add-package-input rec-add-package-input-readonly"
                      value={endDate || ''}
                      readOnly
                      tabIndex={-1}
                    />
                  )}
                </div>
              </div>

              <div className="rec-add-package-col">
                <div className="owner-form-group">
                  <label className="rec-add-package-label" htmlFor="add-package-package">
                    Packages:<span className="rec-add-package-required">*</span>
                  </label>
                  <select
                    id="add-package-package"
                    className="owner-form-select rec-add-package-input"
                    value={packageId}
                    onChange={(e) => setPackageId(e.target.value)}
                    required={activePackages.length > 0}
                    disabled={activePackages.length === 0}
                  >
                    {activePackages.length === 0 ? (
                      <option value="">No packages available</option>
                    ) : (
                      activePackages.map((pkg) => (
                        <option key={pkg.id} value={pkg.id}>
                          {packageOptionLabel(pkg)}
                        </option>
                      ))
                    )}
                  </select>
                  {isFirstTimeMember && nextPackageSerial != null ? (
                    <p className="owner-placeholder-text" style={{ margin: '6px 0 0' }}>
                      Next serial for this package: <strong>{formatSerialDisplay(nextPackageSerial)}</strong>
                      {selectedPackage?.serialNo ? (
                        <> (series from {formatSerialDisplay(selectedPackage.serialNo)})
                        </>
                      ) : null}
                    </p>
                  ) : null}
                </div>

                <div className="owner-form-group">
                  <label className="rec-add-package-label" htmlFor="add-package-duration">
                    Duration:<span className="rec-add-package-required">*</span>
                  </label>
                  <select
                    id="add-package-duration"
                    className="owner-form-select rec-add-package-input"
                    value={durationMonths}
                    onChange={(e) => setDurationMonths(Number(e.target.value) || 1)}
                    required
                  >
                    {SUBSCRIPTION_DURATION_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="owner-form-group">
                  <label className="rec-add-package-label" htmlFor="add-package-price">
                    Package Price:
                  </label>
                  <input
                    id="add-package-price"
                    type="text"
                    inputMode="decimal"
                    className="owner-form-input rec-add-package-input"
                    value={packagePriceInput ?? ''}
                    onChange={(e) => setPackagePriceInput(e.target.value)}
                    placeholder="Optional — leave blank (shows as 0 Rs)"
                    disabled={complimentary}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="owner-card rec-panel-card rec-payment-details-card">
            <h2 className="rec-add-package-title">Payment Details</h2>
            {complimentary ? (
              <p className="owner-placeholder-text" style={{ margin: 0 }}>
                Complimentary subscription — no payment will be recorded.
              </p>
            ) : (
            <div className="rec-payment-details-grid">
              <SaleAmountFields
                idPrefix="subscription"
                totalAmount={String(finalAmount)}
                totalReadOnly
                paymentSplits={paymentSplits}
                onPaymentSplitsChange={setPaymentSplits}
                onSplitsTouched={() => { splitsTouchedRef.current = true; }}
              />

              <div className="owner-form-group rec-payment-note-group rec-ancillary-sale-full">
                <label className="rec-add-package-label" htmlFor="payment-note">
                  Payment Note:
                </label>
                <textarea
                  id="payment-note"
                  className="owner-form-input owner-form-textarea rec-add-package-input"
                  rows={3}
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder="Optional note for this payment..."
                />
              </div>
            </div>
            )}
          </div>
          </div>

          <div className="rec-sub-summary">
            <div className="rec-sub-summary-header">Summary</div>
            <div className="rec-sub-summary-body">
              <div className="rec-sub-summary-row">
                <span>Package name</span>
                <strong>{selectedPackage?.name || '—'}</strong>
              </div>
              <div className="rec-sub-summary-row">
                <span>Duration</span>
                <strong>{selectedDurationLabel}</strong>
              </div>
              <div className="rec-sub-summary-row">
                <span>Package price</span>
                <strong>{complimentary ? 'Complimentary' : formatCurrency(packagePrice)}</strong>
              </div>
              {!complimentary && (
              <div className="rec-sub-summary-row">
                <span>Discount %</span>
                <strong>{formatCurrency(discountAmount)}</strong>
              </div>
              )}

              <div className="rec-sub-summary-row">
                <span>Entry date</span>
                <strong>{formatDisplayDate(entryDate)}</strong>
              </div>
              <div className="rec-sub-summary-row">
                <span>Membership start</span>
                <strong>{datesTbc ? 'TBC' : formatDisplayDate(startDate)}</strong>
              </div>
              <div className="rec-sub-summary-row">
                <span>Membership end</span>
                <strong>{datesTbc ? 'TBC' : endDateLabel}</strong>
              </div>

              {!complimentary && (
              <div className="rec-sub-summary-field">
                  <input
                  id="summary-discount"
                    type="text"
                    inputMode="decimal"
                    className="owner-form-input"
                  value={discountPercent ?? ''}
                  onChange={(e) => setDiscountPercent(e.target.value)}
                  placeholder="Discount %"
                  />
                </div>
              )}

              {!complimentary && isFirstTimeMember && (
              <div className="rec-sub-summary-block">
                <label className="rec-sub-summary-check">
                  <input
                    type="checkbox"
                    checked={registrationFeeEnabled}
                    onChange={(e) => setRegistrationFeeEnabled(e.target.checked)}
                  />
                  <span>Registration Fee</span>
                  <span className="rec-sub-summary-badge">First-time member</span>
                </label>
                {registrationFeeEnabled && (
                  <>
                    <div className="rec-sub-summary-input-wrap">
                      <MdCardMembership className="rec-sub-summary-input-icon" />
                      <input
                        type="text"
                        inputMode="decimal"
                        className="owner-form-input rec-sub-summary-input"
                        value={registrationFeeAmount ?? ''}
                        onChange={(e) => setRegistrationFeeAmount(e.target.value)}
                        placeholder="Registration fee"
                      />
                    </div>
                    <div className="rec-sub-summary-row rec-sub-summary-subtotal">
                      <span>Registration Fee:</span>
                      <strong>{formatCurrency(registrationFeeTotal)}</strong>
                </div>
                  </>
                )}
            </div>
              )}

              <div className="rec-sub-summary-total">
                <span>Total:</span>
              <strong>{complimentary ? 'Complimentary' : formatCurrency(finalAmount)}</strong>
            </div>

              <div className="rec-sub-summary-actions">
                <button
                  type="submit"
                  className="owner-btn owner-btn-primary"
                  disabled={submitting || !customer.trim() || activePackages.length === 0}
                >
                {submitting ? 'Saving…' : 'Complete Sale & Generate Invoice'}
              </button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={resetForm}>
                New Sale
              </button>
              </div>
            </div>
          </div>
        </div>
      </form>
      )}

      {receipt && (
        <div className="subscription-modal-overlay" onClick={() => setReceipt(null)}>
          <div
            className="subscription-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Invoice & Receipt</h2>
              <button
                type="button"
                className="subscription-modal-close"
                onClick={() => setReceipt(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <InvoiceSummary receipt={receipt} />
            <div className="subscription-form-actions">
              <button
                type="button"
                className="owner-btn owner-btn-primary"
                onClick={() => setReceipt(printSubscriptionReceipt(receipt) || receipt)}
              >
                Print Thermal Receipt
              </button>
              <button
                type="button"
                className="owner-btn owner-btn-secondary owner-btn-sm"
                onClick={() => setReceipt(exportSubscriptionReceiptPdf(receipt) || receipt)}
              >
                Export PDF
              </button>
              <button
                type="button"
                className="owner-btn owner-btn-secondary owner-btn-sm"
                onClick={() => setReceipt(exportSubscriptionReceiptCsv(receipt) || receipt)}
              >
                Export CSV
              </button>
              <button
                type="button"
                className="owner-btn owner-btn-secondary owner-btn-sm"
                onClick={() => setReceipt(exportSubscriptionReceiptExcel(receipt) || receipt)}
              >
                Export Excel
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setReceipt(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subscriptions;
